import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Search, Plus, Eye, Trash2 } from 'lucide-react';
import { BillingSection } from './BillingModule';
import { InvoiceModal } from './InvoiceModal';
import { Invoice } from '../../types';
interface Props {
  filter: BillingSection;
  onNavigate: (section: BillingSection) => void;
}
export const Invoices: React.FC<Props> = ({
  filter,
  onNavigate
}) => {
  const {
    invoices,
    patients,
    walkInClients,
    currentUser,
    deleteInvoice
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewingInvoice, setViewingInvoice] = useState<Invoice | null>(null);
  const [deletingInvoiceId, setDeletingInvoiceId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const isAdministrator = currentUser?.role === 'Administrator';
  const getStatusFilter = () => {
    switch (filter) {
      case 'invoices-draft':
        return 'Draft';
      case 'invoices-sent':
        return 'Sent';
      case 'invoices-unpaid':
        return 'Unpaid';
      case 'invoices-paid':
        return 'Paid';
      case 'invoices-partial':
        return 'Partially Paid';
      case 'invoices-overdue':
        return 'Overdue';
      default:
        return 'All';
    }
  };
  const statusFilter = getStatusFilter();
  const filteredInvoices = invoices.filter(inv => {
    if (statusFilter !== 'All' && inv.status !== statusFilter) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    if (inv.invoiceNumber.toLowerCase().includes(q)) return true;
    return getCustomerName(inv.customerId, inv.customerType, inv.customerName).toLowerCase().includes(q);
  });
  function getCustomerName(customerId: string, type: 'Patient' | 'WalkIn', savedName?: string) {
    if (type === 'Patient') {
      const p = patients.find(p => p.id === customerId);
      return p ? `${p.firstName} ${p.lastName}` : 'Unknown Patient';
    }
    return savedName || walkInClients.find(c => c.id === customerId)?.name || 'Walk-in client';
  }
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZM', {
      style: 'currency',
      currency: 'ZMW'
    }).format(amount);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800';
      case 'Unpaid':
        return 'bg-amber-100 text-amber-800';
      case 'Partially Paid':
        return 'bg-blue-100 text-blue-800';
      case 'Overdue':
        return 'bg-red-100 text-red-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      case 'Sent':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getTitle = () => {
    switch (filter) {
      case 'invoices-draft':
        return 'Draft Invoices';
      case 'invoices-sent':
        return 'Sent Invoices';
      case 'invoices-unpaid':
        return 'Unpaid Invoices';
      case 'invoices-paid':
        return 'Paid Invoices';
      case 'invoices-partial':
        return 'Partially Paid Invoices';
      case 'invoices-overdue':
        return 'Overdue Invoices';
      default:
        return 'Invoices';
    }
  };
  const handleDelete = async (invoice: Invoice) => {
    const confirmed = window.confirm(`Delete invoice ${invoice.invoiceNumber}? This permanently removes the invoice and any linked payments or credit notes. This cannot be undone.`);
    if (!confirmed) return;
    setDeleteError(null);
    setDeletingInvoiceId(invoice.id);
    const result = await deleteInvoice(invoice.id);
    setDeletingInvoiceId(null);
    if (!result.ok) setDeleteError(result.error || 'Invoice could not be deleted.');
  };
  return <div className="space-y-6" data-id="element-1326" data-mp="3agbnkq68utb2">
      <div className="flex justify-between items-center" data-id="element-1327" data-mp="3ahd06z2wrnuw">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1328" data-mp="1luif69yn2er0">{getTitle()}</h1>
        <Button onClick={() => onNavigate('new-invoice')} data-id="element-1329" data-mp="1gu4ghiixgh5n">
          <Plus className="w-4 h-4 mr-2" data-id="element-1330" data-mp="1gwqp7lhk12pg" /> New Invoice
        </Button>
      </div>

      <Card noPadding data-id="element-1331" data-mp="1ctatqsh818ec">
        <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-1332" data-mp="1cuvnifvponb5">
          <div className="relative max-w-md" data-id="element-1333" data-mp="17viq5177rxub">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1334" data-mp="24jytq175s7qf">
              <Search className="h-5 w-5 text-gray-400" data-id="element-1335" data-mp="20ivengwxqfq9" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by invoice number or customer name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1336" data-mp="20kpx8okqhvr6" />
          </div>
          {deleteError && <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-id="element-1337" data-mp="2qugmhlsz4vk6">
              {deleteError}
            </p>}
        </div>

        <div className="overflow-x-auto" data-id="element-1338" data-mp="34sexch8369ux">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1339" data-mp="34t6lj6zct5b6">
            <thead className="bg-gray-50" data-id="element-1340" data-mp="39rzzt1mgt0ce">
              <tr data-id="element-1341" data-mp="39qymsrlw23s1">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1342" data-mp="39pds91zkqku6">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1343" data-mp="3jich8kyzgdeb">
                  Invoice#
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1344" data-mp="3shzm5gqx81c4">
                  Customer
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1345" data-mp="1c7uue1eqinq">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1346" data-mp="1tdrcih7a50rs">
                  Amount
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1347" data-mp="276pg2qtvlz6f">
                  Balance Due
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1348" data-mp="2h1hcvsfmwi05">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1349" data-mp="2q2ffc6afchgm">
              {filteredInvoices.length > 0 ? filteredInvoices.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(inv => <tr key={inv.id} className="hover:bg-gray-50" data-id="element-1350" data-mp="qsqvli7fdngu">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1351" data-mp="32e9jp4dkozas">
                        {new Date(inv.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600" data-id="element-1352" data-mp="2tkd0nyfpg2zl">
                        {inv.invoiceNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1353" data-mp="2jni1ddblxyk9">
                        {getCustomerName(inv.customerId, inv.customerType, inv.customerName)}
                        <span className="ml-2 text-xs text-gray-400 font-normal" data-id="element-1354" data-mp="2envs1eywh2gq">
                          ({inv.customerType})
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-1355" data-mp="cwsb67yf2rnq">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(inv.status)}`} data-id="element-1356" data-mp="cuo97vk2w2q3">
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900" data-id="element-1357" data-mp="2n16wq9nso317">
                        {formatCurrency(inv.totalAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-red-600" data-id="element-1358" data-mp="2e3whkd7uvtr0">
                        {formatCurrency(inv.balanceDue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1359" data-mp="244nsnyrul5bi">
                        <button type="button" className="mx-1 rounded p-1 text-gray-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500" title="View invoice" aria-label={`View invoice ${inv.invoiceNumber}`} onClick={() => setViewingInvoice(inv)} data-id="element-1360" data-mp="1z6m1sro6k1ph">
                          <Eye className="w-4 h-4" data-id="element-1361" data-mp="9txheg1cdab7" />
                        </button>
                        {isAdministrator && <button type="button" className="mx-1 rounded p-1 text-gray-400 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:cursor-not-allowed disabled:opacity-50" title="Delete invoice" aria-label={`Delete invoice ${inv.invoiceNumber}`} disabled={deletingInvoiceId === inv.id} onClick={() => handleDelete(inv)} data-id="element-1362" data-mp="jmmq535ica2u">
                            <Trash2 className="w-4 h-4" data-id="element-1363" data-mp="17ds75ln6p8eq" />
                          </button>}
                      </td>
                    </tr>) : <tr data-id="element-1364" data-mp="8za3txji8gx">
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500" data-id="element-1365" data-mp="59wr436tcwgu">
                    No invoices found.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {viewingInvoice && <InvoiceModal invoice={viewingInvoice} onClose={() => setViewingInvoice(null)} data-id="element-1366" data-mp="3din8r7rtvquy" />}
    </div>;
};